import {
  api,
  setToken,
  clearToken,
  getToken,
  startSessionKeepalive,
  stopSessionKeepalive,
} from './api';
import { getInitData } from './initData';
import type { TelegramAuthResponse } from './types';

export { getInitData };

/**
 * Exchange initData for a JWT. Backend validates HMAC + upserts user.
 * Successfully authenticated token is stored in localStorage.
 */
export async function authenticate(): Promise<TelegramAuthResponse> {
  const init_data = getInitData();
  if (!init_data) {
    throw new Error('no initData — open this app from Telegram');
  }
  const resp = await api.post<TelegramAuthResponse>('/v1/auth/telegram', {
    init_data,
  });
  setToken(resp.access_token);
  // Keep the access token alive while the app is open so it never
  // silently expires under an idle/backgrounded session.
  startSessionKeepalive(resp.expires_in);
  return resp;
}

export async function logout(): Promise<void> {
  // Best-effort: tell the backend to wipe the refresh row + clear
  // the cookie. We don't gate on success because the local clearToken
  // must run regardless — a stuck network shouldn't pin the user
  // to a session they're trying to end.
  try {
    await api.post('/v1/auth/logout');
  } catch {
    // ignore — server-side cleanup is opportunistic
  }
  stopSessionKeepalive();
  clearToken();
}

export function isAuthenticated(): boolean {
  return getToken() !== null;
}

/** Raw payload Telegram's Login Widget hands to `window.onTelegramAuth`. */
export interface LoginWidgetPayload {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

/**
 * Browser-cabinet sign-in. Exchanges a Telegram Login Widget payload for
 * a JWT (no `idh` claim — there's no WebApp initData in a plain browser;
 * the binding middleware passes these through). Mirrors `authenticate()`
 * but for the desktop/browser surface.
 */
export async function authenticateBrowser(
  payload: LoginWidgetPayload,
): Promise<TelegramAuthResponse> {
  const resp = await api.post<TelegramAuthResponse>(
    '/v1/auth/telegram-login',
    { ...payload },
  );
  setToken(resp.access_token);
  startSessionKeepalive(resp.expires_in);
  return resp;
}

/**
 * Re-establish a browser session on a cold page load from the HttpOnly
 * refresh cookie (the access token isn't reliably persisted across
 * reloads). Mints a fresh access token + arms keepalive. Returns false
 * when there's no valid cookie — the caller then routes to /login.
 */
export async function bootstrapBrowserSession(): Promise<boolean> {
  try {
    const resp = await api.post<{ access_token: string; expires_in: number }>(
      '/v1/auth/refresh',
    );
    setToken(resp.access_token);
    startSessionKeepalive(resp.expires_in);
    return true;
  } catch {
    clearToken();
    return false;
  }
}
